import { Button, HubCampaignsService, Selector, ToastHelper } from '@hub/shared'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import './research-pre-recruitment.component.scoped.scss'

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  typeFormLink: string
  solution: string
  campaignId: string
  squidId: string
  idNetwork: string
  onChange?: (value: any) => any
}

export default ({ typeFormLink = '', solution = '', campaignId, squidId, idNetwork, onChange, ...props }: Props) => {
  const [t] = useTranslation('researchPreRecruitment')
  const toast = useMemo(() => new ToastHelper().toast, [])
  const hubCampaignsService = useMemo(() => new HubCampaignsService(), [])
  const [loading, setLoading] = useState(false)
  const [answered, setAnswered] = useState(false)
  const [idNetworkState, setIdNetworkState] = useState('')
  const [state, setState] = useState(null)

  useEffect(() => {
    if (idNetworkState !== idNetwork) {
      setIdNetworkState(idNetwork)
      setAnswered(false)
    }
  }, [idNetworkState, idNetwork])

  const getSurveyAnswered = () => {
    setLoading(true)
    hubCampaignsService
      .getSurveyAnswered(campaignId, idNetworkState)
      .then((e) => {
        if (!e) {
          toast.error(t('notAnswered'), {
            closeButton: true,
            className: 'full',
            position: `${window.innerWidth > 991 ? 'top' : 'bottom'} center`,
            duration: 3600000,
            notOverClick: true,
            immediately: true,
          })
        }
        setAnswered(e)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const solutionReturn = useCallback(
    (renderCheck?: boolean) => {
      if (renderCheck) {
        return solution.toLocaleLowerCase() !== 'survey'
      }
      return solution.toLocaleLowerCase() !== 'survey'
        ? state?.notGuarantee && state?.awareAnswerForm && answered
        : state?.awareAnswerForm && answered
    },
    [solution, state, answered]
  )

  useEffect(() => {
    if (onChange && typeof onChange === 'function' && typeFormLink) {
      onChange(solutionReturn())
    }
  }, [state, answered, onChange, typeFormLink, solutionReturn])

  const handleChange = useCallback(
    (field: string, value: boolean) => {
      setState({ ...state, [field]: value })
    },
    [state]
  )

  if (!typeFormLink) {
    return null
  }

  return (
    <div>
      <section className="research-pre-recruitment" {...props}>
        <div className="research-box">
          <p className="display-flex align-items-center">
            <strong className="mr-2">{t('boxTitle')}</strong>
            {!answered ? <i className="fa-solid fa-diamond-exclamation" style={{ color: 'var(--gold)', fontSize: '1.2rem' }}></i> : null}
          </p>
          <p className="mt-2">{t('boxText')}</p>
          <div className="research-buttons mt-3">
            {answered ? (
              <span className="display-flex align-items-center">
                <i className="mr-1 fa-solid fa-circle-check" style={{ color: 'var(--green)' }}></i> {t('answered')}
              </span>
            ) : (
              <div className="container-research-buttons display-flex">
                <Button
                  id="button-already-answered"
                  onClick={() => getSurveyAnswered()}
                  loading={loading}
                  size="sm"
                  color="transparent"
                  textColor="var(--primary_color)"
                  loaderColor="var(--primary_color)"
                >
                  <span>{t('alreadyAnswered')}</span>
                </Button>
                <a href={`${typeFormLink}?squidId=${squidId}&campaignId=${campaignId}&socialId=${idNetwork}`} target="_blank">
                  <Button id="button-to-answer" size="sm">
                    <span>
                      <i className="fa-solid fa-up-right-from-square" style={{ color: 'var(--text-color)' }}></i> {t('toAnswer')}
                    </span>
                  </Button>
                </a>
              </div>
            )}
          </div>
          <hr />
          <Selector
            name="aware-answer-form-check"
            id="aware-answer-form-check"
            checked={state?.awareAnswerForm}
            onChange={() => handleChange('awareAnswerForm', !state?.awareAnswerForm)}
            type="checkbox"
            errorSpan={false}
            rightLabel={<p>{t('awareAnswerForm')}</p>}
          />
        </div>
      </section>
      {solutionReturn(true) ? (
        <section className="research-pre-recruitment" {...props}>
          <div className="not-guarantee">
            <Selector
              name="not-guarantee-check"
              id="not-guarantee-check"
              checked={state?.notGuarantee}
              onChange={() => handleChange('notGuarantee', !state?.notGuarantee)}
              type="checkbox"
              errorSpan={false}
              rightLabel={<p>{t('notGuarantee')}</p>}
              className="not-guarantee"
              boxBgSecondary
            />
          </div>
        </section>
      ) : null}
    </div>
  )
}
